import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, Edit2 } from "lucide-react";
import { toast } from "sonner";
import { Modal, Field, inputCls, StatusBadge } from "@/components/admin/FormBits";

const CATEGORIES = ["Raw Materials", "Packaging", "ICT Hardware", "Software", "Services", "Other"];
const EMPTY = { name: "", contact_person: "", email: "", phone: "", address: "", category: "Raw Materials", payment_terms: "Cash on delivery", status: "active", notes: "" };

export default function AdminSuppliers() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [search, setSearch] = useState("");

    function load() { api.get("/admin/suppliers").then((r) => setItems(r.data)).catch(() => {}); }
    useEffect(load, []);

    async function save(payload) {
        try {
            if (payload.id) { await api.put(`/admin/suppliers/${payload.id}`, payload); toast.success("Supplier updated"); }
            else { await api.post("/admin/suppliers", payload); toast.success("Supplier added"); }
            setEditing(null); load();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    }

    async function remove(id) {
        if (!confirm("Delete this supplier?")) return;
        try { await api.delete(`/admin/suppliers/${id}`); toast.success("Deleted"); load(); }
        catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    }

    const visible = items.filter((s) => `${s.name} ${s.contact_person} ${s.category}`.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="p-8" data-testid="admin-suppliers">
            <div className="flex items-center justify-between mb-1">
                <h1 className="font-display text-3xl font-bold">Suppliers</h1>
                <button onClick={() => setEditing({ ...EMPTY })} className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 font-semibold" data-testid="add-supplier-btn">
                    <Plus className="w-4 h-4" /> Add supplier
                </button>
            </div>
            <p className="text-sm text-muted-foreground mb-6">{items.length} supplier{items.length !== 1 ? "s" : ""} · {items.filter((s) => s.status === "active").length} active</p>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search suppliers…" className={`${inputCls} max-w-sm mb-4`} data-testid="supplier-search" />
            <div className="bg-white border border-border overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-muted text-left"><tr>
                        <th className="px-4 py-3">Supplier</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Contact</th><th className="px-4 py-3">Terms</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th>
                    </tr></thead>
                    <tbody>
                        {visible.map((s) => (
                            <tr key={s.id} className="border-t border-border" data-testid={`supplier-row-${s.id}`}>
                                <td className="px-4 py-3"><div className="font-semibold">{s.name}</div><div className="text-xs text-muted-foreground">{s.address}</div></td>
                                <td className="px-4 py-3">{s.category}</td>
                                <td className="px-4 py-3 text-xs"><div>{s.contact_person}</div><div>{s.phone}</div><div className="text-muted-foreground">{s.email}</div></td>
                                <td className="px-4 py-3 text-xs">{s.payment_terms}</td>
                                <td className="px-4 py-3"><StatusBadge value={s.status} /></td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <button onClick={() => setEditing(s)} className="p-2 hover:text-primary" data-testid={`edit-supplier-${s.id}`}><Edit2 className="w-4 h-4" /></button>
                                    <button onClick={() => remove(s.id)} className="p-2 hover:text-primary" data-testid={`delete-supplier-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                                </td>
                            </tr>
                        ))}
                        {visible.length === 0 && <tr><td colSpan="6" className="text-center py-12 text-muted-foreground">No suppliers yet. Add your first supplier to start tracking purchases.</td></tr>}
                    </tbody>
                </table>
            </div>
            {editing && <SupplierForm initial={editing} onClose={() => setEditing(null)} onSave={save} />}
        </div>
    );
}

function SupplierForm({ initial, onClose, onSave }) {
    const [form, setForm] = useState(initial);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    return (
        <Modal title={`${form.id ? "Edit" : "New"} supplier`} onClose={onClose} onSubmit={() => onSave(form)} testId="supplier-form">
            <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Company name *"><input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="supplier-name" /></Field>
                <Field label="Category"><select value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></Field>
                <Field label="Contact person"><input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} className={inputCls} data-testid="supplier-contact" /></Field>
                <Field label="Phone"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="supplier-phone" /></Field>
                <Field label="Email"><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} /></Field>
                <Field label="Payment terms"><input value={form.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} className={inputCls} placeholder="e.g. Net 30" /></Field>
                <Field label="Address"><input value={form.address} onChange={(e) => set("address", e.target.value)} className={inputCls} /></Field>
                <Field label="Status"><select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls} data-testid="supplier-status"><option value="active">Active</option><option value="inactive">Inactive</option></select></Field>
            </div>
            <div className="mt-3"><Field label="Notes"><textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className={inputCls} /></Field></div>
        </Modal>
    );
}
